import { emailAddressSchema } from "@/components/Forms/schema/emailAddressSchema.ts";
import useAuthenticatedFetch from "@/hooks/useAuthenticatedFetch.ts";
import { apiUrl } from "@/utils/api.ts";
import { type UseQueryResult, useQuery } from "@tanstack/react-query";
import { createDataSelector, createResourceCollectionSelector } from "jsonapi-zod-query";
import type {
    MRT_ColumnFiltersState,
    MRT_PaginationState,
    MRT_SortingState,
} from "material-react-table";

const resourceSelector = createResourceCollectionSelector({
    type: "emailAddress",
    attributesSchema: emailAddressSchema,
});

const companionSelector = createDataSelector(resourceSelector);

export type EmailAddresses = ReturnType<typeof companionSelector>;
export type MrtEmailAddress = EmailAddresses[number];

type Props = {
    companyUUID: string;
    columnFilters: MRT_ColumnFiltersState;
    globalFilter: string;
    pagination: MRT_PaginationState;
    sorting: MRT_SortingState;
};

export const useFetchMrtEmailsForCompany = ({
    companyUUID,
    columnFilters,
    globalFilter,
    pagination,
    sorting,
}: Props): UseQueryResult<EmailAddresses> => {
    const fetch = useAuthenticatedFetch();

    return useQuery({
        queryKey: [
            "company-email-table-data",
            companyUUID,
            columnFilters,
            globalFilter,
            pagination.pageIndex,
            pagination.pageSize,
            sorting,
        ],
        queryFn: async () => {
            const fetchURL = apiUrl("/company/email/for-company");

            const response = await fetch(fetchURL.href, {
                method: "POST",
                headers: {
                    "Content-Type": "application/json",
                },
                body: JSON.stringify({
                    companyUUID: companyUUID,
                    start: pagination.pageIndex * pagination.pageSize,
                    size: pagination.pageSize,
                    filters: columnFilters ?? [],
                    globalFilter: globalFilter ?? "",
                    sorting: sorting ?? [],
                }),
            });

            return await response.json();
        },
        select: companionSelector,
    });
};
